import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { AgricolaService } from '../../../services/agricola.service';
import { AgricolaUpdatePaymentDialogComponent } from './agricola-update-payment-dialog/agricola-update-payment-dialog.component';
import { AgricolaUpdateClientDialogComponent } from './agricola-update-client-dialog/agricola-update-client-dialog.component';
import { AgricolaCancelDialogComponent } from './agricola-cancel-dialog/agricola-cancel-dialog.component';
import { AgricolaToastType } from '../../../interfaces/agricola.interface';

export interface PolicyBasicInfo {
  policyStatus: string;
  policyNumber: string;
  productType: string;
  productCode: string;
  product: string;
  insuredAmount: string;
  insuredName: string;
  uniqueNumber: string;
  spouseName: string;
  agencyCode: string;
  vendorCode: string;
  dui: string;
  cancellationReason: string;
  cancellationDate: string;
  startDate: string;
  address: string;
  phone: string;
  mobile: string;
  email: string;
  // Personal data
  birthday: string;
  gender: string;
  relationshipStatus: string;
  memberClassification: string;
  taxNumber: string;
  phone2: string;
}

export interface PolicyPaymentInfo {
  policyStatus: string;
  uniqueNumber: string;
  chargeAccount: string;
  paidPremiums: string;
  totalPaidPremiums: string;
  validityEnd: string;
  refundAmount: string;
  delinquencyDays: string;
  lastCollectionDate: string;
  delinquencyStatus: string;
  totalDelinquentPremiums: string;
  lastPaymentDate: string;
  nextPaymentDate: string;
  paymentMethod: string;
  premiumValue: string;
  emissionOrigin: string;
  paymentStatus: string;
}

export interface PaymentDetailRow {
  chargeDate: string;
  month: string;
  total: string;
  status: string;
  authorizationCode: string;
  comment: string;
}

export interface AddonRow {
  name: string;
  price: string;
  status: string;
  services: string[];
}

export interface AccountRow {
  codCtaTDC: string;
  accountNumber: string;
  accountType: string;
  accountName: string;
  status: string;
}

export interface TransactionRow {
  transaction: string;
  requestDate: string;
  processDate: string;
  amount: string;
  agencyName: string;
  agencyUser: string;
}

const TRANSACTION_EQUIVALENCES: Record<string, string> = {
  change_plan: 'Cambio de Plan a Solicitud del Cliente',
  product_change: 'Cambio de producto',
  add_beneficiaries: 'Beneficiarios',
  dependants_transactions: 'Dependientes',
  payment_method: 'Medio de pago',
  references: 'Referencias',
  crossSell: 'crossSell',
  welcome: 'welcome',
  cancellation: 'Cancelación',
  reinstatement: 'Reactivación',
  upsell_upgrade: 'Upgrade',
  upsell_only: 'Upsell',
  downgrade: 'Downgrade',
  retention_product_change: 'Retención cambio de producto',
  retention_sale: 'Retención reventa',
  retention_change_plan: 'Retención cambio de plan',
  retention_assistance: 'Retención con asistencia',
  'Nueva póliza': 'Nueva póliza',
  complaints: 'Complaints',
  quick_note: 'Nota rápida',
  update_customer_data: 'Datos personales',
  send_policy: 'Enviar Póliza',
  strategy: 'Estrategias',
  hang_up: 'Finalizar llamada',
  transfer_call: 'Transferir llamada',
  references_retention_sale: 'Referencia retención reventa',
  add_dependants: 'Agregar dependiente',
  update_customer_data_dp: 'Actualizar dependiente',
  rmi_dependants: 'Eliminar dependiente',
  send_policy_adisa: 'Enviar Póliza Internacional de Seguros',
  assistance_change_plan: 'Cambio de Plan a Solicitud del Cliente',
  assistance_send_teledoctor_user: 'Crear usuario Teledoctor',
  assistance_product_change: 'Cambio de producto',
  assistances_dependants_transactions: 'Dependientes',
  assistance_add_dependents: 'Agregar dependiente',
  assistance_edit_dependents: 'Actualizar dependiente',
  assistance_remove_dependents: 'Eliminar dependiente',
  send_teledoctor_user: 'Envio de usuario Teledoctor',
  assistance_cancelation: 'Cancelación',
  upgrade_apolo: 'Upgrade',
  downgrade_apolo: 'Downgrade',
  assistance_retention_product_change: 'Retención cambio de producto (S2+)',
  assistance_retention_product_change_no_charge: 'Retención cambio de producto (APOLO)',
  assistance_retention_sale: 'Retención reventa',
  assistance_retention_change_plan: 'Retención cambio de plan',
  send_welcome_kit: 'Envío de Kit',
  send_policy_adisa_assistance: 'Enviar Póliza Internacional de Seguros Asistencia',
  assistance_quick_note: 'Nota rápida',
  update_assistance_customer_data: 'Datos personales',
  strategy_apolo: 'Estrategias',
  hang_up_apolo: 'Finalizar llamada',
  transfer_call_apolo: 'Transferir llamada',
  retention_apolo: 'Retención',
  assistance_reinstatement: 'Reactivación',
  assistance_payment_method_change: 'Cambio de método de pago'
};

const PAYMENT_STATUS: Record<number, string> = {
  0: 'Cancelado',
  1: 'Pagado',
  2: 'Pendiente',
  3: 'Procesando'
};

@Component({
  selector: 'app-agricola-policy-detail',
  templateUrl: './agricola-policy-detail.component.html',
  styleUrls: ['./agricola-policy-detail.component.css'],
  standalone: false
})
export class AgricolaPolicyDetailComponent implements OnInit {

  policyNumber = '';
  // Oculta la sección "¿Qué deseas hacer?" (banner y acciones) sin remover su lógica
  showActions = false;
  isLoading = false;
  showToast = false;
  toastMessage = '';
  toastType: AgricolaToastType = 'success';
  private rawMember: any = null;
  private rawPlan: any = null;
  private rawSaleId = 0;
  private creditNumber = '';
  currentPaymentAccount: AccountRow | null = null;

  basicInfo: PolicyBasicInfo = {
    policyStatus: '', policyNumber: '', productType: '', productCode: '',
    product: '', insuredAmount: '', insuredName: '', uniqueNumber: '',
    spouseName: '', agencyCode: '', vendorCode: '', dui: '',
    cancellationReason: '', cancellationDate: '', startDate: '',
    address: '', phone: '', mobile: '', email: '',
    birthday: '', gender: '', relationshipStatus: '',
    memberClassification: '', taxNumber: '', phone2: ''
  };

  paymentInfo: PolicyPaymentInfo = {
    policyStatus: '', uniqueNumber: '', chargeAccount: '',
    paidPremiums: '', totalPaidPremiums: '', validityEnd: '', refundAmount: '',
    delinquencyDays: '', lastCollectionDate: '', delinquencyStatus: '',
    totalDelinquentPremiums: '', lastPaymentDate: '', nextPaymentDate: '',
    paymentMethod: '', premiumValue: '', emissionOrigin: '', paymentStatus: ''
  };

  allPaymentRows: PaymentDetailRow[] = [];
  pagedPaymentRows: PaymentDetailRow[] = [];
  paymentTotalItems = 0;
  paymentCurrentPage = 1;
  paymentPageSize = 10;

  addonRows: AddonRow[] = [];
  addonsTotal: number | null = null;
  totalWithAddons: number | null = null;
  accountRows: AccountRow[] = [];

  allTransactionRows: TransactionRow[] = [];
  pagedTransactionRows: TransactionRow[] = [];
  transactionTotalItems = 0;
  transactionCurrentPage = 1;
  transactionPageSize = 10;

  pageSizeOptions = [5, 10, 25, 50];

  constructor(
    private route: ActivatedRoute,
    private location: Location,
    private dialog: MatDialog,
    private agricolaService: AgricolaService
  ) {}

  ngOnInit(): void {
    this.policyNumber = this.route.snapshot.paramMap.get('policyNumber') ?? '';
    this.loadPolicyDetail(this.policyNumber);
  }

  get isPolicyCancelled(): boolean {
    const status = this.basicInfo.policyStatus?.toUpperCase();
    return status === 'CANCELADA' || status === 'CANCELADO';
  }

  goBack(): void {
    this.location.back();
  }

  hideToast(): void {
    this.showToast = false;
  }

  private notify(success: boolean, successMsg = 'Operación realizada con éxito', errorMsg = 'Ocurrió un error. Intenta nuevamente.'): void {
    this.toastMessage = success ? successMsg : errorMsg;
    this.toastType    = success ? 'success' : 'error';
    this.showToast    = true;
    setTimeout(() => { this.showToast = false; }, 4000);
  }

  openUpdateClientDialog(): void {
    const ref = this.dialog.open(AgricolaUpdateClientDialogComponent, {
      width: '700px',
      height: 'auto',
      maxWidth: '100vw',
      maxHeight: '95vh',
      disableClose: true,
      data: {
        address:             this.rawMember?.address ?? '',
        phone:               this.basicInfo.phone,
        mobile:              this.basicInfo.mobile,
        email:               this.basicInfo.email,
        dui:                 this.basicInfo.dui,
        memberId:            this.rawMember?.id ?? 0,
        firstName:           this.rawMember?.first_name ?? '',
        middleName:          this.rawMember?.middle_name ?? null,
        lastName:            this.rawMember?.last_name ?? '',
        lastName2:           this.rawMember?.last_name_2 ?? null,
        marriedName:         this.rawMember?.married_name ?? null,
        birthday:            this.rawMember?.birthday ?? null,
        gender:              this.rawMember?.gender ?? null,
        relationshipStatus:  this.rawMember?.relationship_status ?? null,
        memberClassification:this.rawMember?.member_classification ?? null,
        phone2:              this.rawMember?.phone2 ?? '',
        taxNumber:           this.rawMember?.tax_number ?? '',
        stateId:             this.rawMember?.state_id ?? null,
        stateDistrictId:     this.rawMember?.state_district_id ?? null,
        clientStatus:        this.rawMember?.status ?? null,
        frontingId:          this.rawPlan?.product?.fronting_id ?? 0,
        planName:            this.rawPlan?.name ?? '',
        assistanceCode:      this.rawSaleId,
        assistanceSaleCode:  this.basicInfo.policyNumber
      }
    });

    ref.afterClosed().subscribe(result => {
      if (result == null) return;
      this.notify(result.success, 'Datos del cliente actualizados correctamente');
      if (result.success) this.loadPolicyDetail(this.policyNumber);
    });
  }

  openCancelDialog(): void {
    const ref = this.dialog.open(AgricolaCancelDialogComponent, {
      width: '700px',
      height: 'auto',
      maxWidth: '100vw',
      maxHeight: '95vh',
      disableClose: true,
      data: {
        policyNumber:       this.basicInfo.policyNumber,
        assistanceCode:     this.rawSaleId,
        memberId:           this.rawMember?.id ?? 0,
        frontingId:         this.rawPlan?.product?.fronting_id ?? 0,
        planName:           this.rawPlan?.name ?? '',
        assistanceSaleCode: this.basicInfo.policyNumber
      }
    });

    ref.afterClosed().subscribe(result => {
      if (result == null) return;
      this.notify(result.success, 'Solicitud de cancelación enviada correctamente');
      if (result.success) this.loadPolicyDetail(this.policyNumber);
    });
  }

  openUpdatePaymentDialog(): void {
    const ref = this.dialog.open(AgricolaUpdatePaymentDialogComponent, {
      width: '700px',
      height: 'auto',
      maxWidth: '100vw',
      maxHeight: '95vh',
      disableClose: true,
      data: {
        managementCode:     this.basicInfo.policyNumber,
        insuredName:        this.basicInfo.insuredName,
        accountRows:        this.accountRows,
        currentAccount:     this.currentPaymentAccount,
        assistanceSaleId:   this.rawSaleId,
        frontingId:         this.rawPlan?.product?.fronting_id ?? 0,
        memberId:           this.rawMember?.id ?? 0,
        planName:           this.rawPlan?.name ?? '',
        assistanceSaleCode: this.basicInfo.policyNumber
      }
    });

    ref.afterClosed().subscribe(result => {
      if (result == null) return;
      this.notify(result.success, 'Método de pago actualizado correctamente');
      if (result.success) this.loadPolicyDetail(this.policyNumber);
    });
  }

  // Payment table pagination
  get paymentTotalPages(): number {
    return Math.ceil(this.paymentTotalItems / this.paymentPageSize);
  }

  get paymentVisiblePages(): number[] {
    return this.buildPageRange(this.paymentCurrentPage, this.paymentTotalPages);
  }

  get paymentMovementsLabel(): string {
    return this.buildMovementsLabel(this.paymentCurrentPage, this.paymentPageSize, this.paymentTotalItems);
  }

  goToPaymentPage(page: number): void {
    if (page < 1 || page > this.paymentTotalPages) return;
    this.paymentCurrentPage = page;
    this.updatePaymentPage();
  }

  onPaymentPageSizeChange(event: Event): void {
    this.paymentPageSize = Number((event.target as HTMLSelectElement).value);
    this.paymentCurrentPage = 1;
    this.updatePaymentPage();
  }

  // Transaction table pagination
  get transactionTotalPages(): number {
    return Math.ceil(this.transactionTotalItems / this.transactionPageSize);
  }

  get transactionVisiblePages(): number[] {
    return this.buildPageRange(this.transactionCurrentPage, this.transactionTotalPages);
  }

  get transactionMovementsLabel(): string {
    return this.buildMovementsLabel(this.transactionCurrentPage, this.transactionPageSize, this.transactionTotalItems);
  }

  goToTransactionPage(page: number): void {
    if (page < 1 || page > this.transactionTotalPages) return;
    this.transactionCurrentPage = page;
    this.updateTransactionPage();
  }

  onTransactionPageSizeChange(event: Event): void {
    this.transactionPageSize = Number((event.target as HTMLSelectElement).value);
    this.transactionCurrentPage = 1;
    this.updateTransactionPage();
  }

  private loadPolicyDetail(policyNumber: string): void {
    this.isLoading = true;
    this.agricolaService.getAssistanceSaleById(policyNumber).subscribe({
      next: (response: any) => {
        const p = response?.data ?? response;
        this.rawMember = p.member ?? null;
        this.rawPlan   = p.plan ?? null;
        this.rawSaleId = p.id ?? 0;

        const currencySymbol = this.rawPlan?.currency?.symbol ?? '';
        const matchedAddonIds: Set<number> = new Set(this.rawPlan?.matched_addon_ids ?? []);
        this.addonRows = (this.rawPlan?.addons ?? [])
          .filter((a: any) => matchedAddonIds.has(a.id))
          .map((a: any) => ({
            name:     a.name ?? '',
            price:    a.base_price != null ? `${a.currency?.symbol ?? currencySymbol} ${a.base_price}` : '',
            status:   'Activo',
            services: (a.services ?? []).map((s: any) => s.name).filter(Boolean)
          }));
        this.addonsTotal      = this.rawPlan?.addons_total      ?? null;
        this.totalWithAddons  = this.rawPlan?.total_with_addons ?? null;
        const isCancelled = !!p.date_canceled || !!p.cancellation_reason;
        this.basicInfo = {
          policyStatus:       isCancelled ? 'CANCELADA' : 'ACTIVA',
          policyNumber:       p.code ?? policyNumber,
          productType:        p.plan?.product?.name ?? '',
          productCode:        p.plan?.product?.assistance_code ?? '',
          product:            p.plan?.name ?? '',
          insuredAmount:      p.plan?.currency?.symbol && p.plan?.base_price != null
                                ? `${p.plan.currency.symbol} ${p.plan.base_price}`
                                : '',
          insuredName:        p.member?.name ?? '',
          uniqueNumber:       '',
          spouseName:         '',
          agencyCode:         '',
          vendorCode:         '',
          dui:                p.member?.national_id ?? '',
          cancellationReason: p.cancellation_reason ?? '',
          cancellationDate:   p.date_canceled ?? '',
          startDate:          this.formatDate(p.date_effective ?? ''),
          address:            p.member?.address ?? '',
          phone:              p.member?.phone1 ?? '',
          mobile:             p.member?.mobile ?? '',
          email:              p.member?.email ?? '',
          birthday:           this.formatDate(p.member?.birthday ?? ''),
          gender:             this.mapGender(p.member?.gender),
          relationshipStatus: this.mapRelationshipStatus(p.member?.relationship_status),
          memberClassification: this.mapMemberClassification(p.member?.member_classification),
          taxNumber:          p.member?.tax_number ?? '',
          phone2:             p.member?.phone2 ?? ''
        };
        this.isLoading = false;

        this.agricolaService.getAssistanceSaleExtraData(this.rawSaleId).subscribe({
          next: (res: any) => {
            const items: any[] = res?.data ?? [];
            const uniqueNumber = items.find((e: any) => e.key_ === 'unique_number')?.value_ ?? null;
            // credit_number is stored alongside unique_number in the sale's extraData
            // (see buildEmissionBody in agricola.service.ts); it identifies which of the
            // client's accounts is the one being charged.
            this.creditNumber = items.find((e: any) => e.key_ === 'credit_number')?.value_ ?? '';
            if (!uniqueNumber) return;

            this.agricolaService.getClientAccounts(uniqueNumber).subscribe({
              next: (accounts: any) => {
                const body: any[] = accounts?.data?.data?.body ?? [];
                this.accountRows = body.map(a => ({
                  codCtaTDC:     a.codCtaTDC?.trim() ?? '',
                  accountNumber: a.numCta?.trim()    ?? '',
                  accountType:   a.tipoCta?.trim()   ?? '',
                  accountName:   a.nombreCta?.trim() ?? '',
                  status:        a.estadoCta?.trim() ?? ''
                }));
                this.matchCurrentPaymentAccount();
              },
              error: () => { this.accountRows = []; }
            });
          },
          error: () => { this.accountRows = []; }
        });

        this.agricolaService.getAssistancePaymentRequirement(this.rawSaleId).subscribe({
          next: (res: any) => {
            const raw = Array.isArray(res) ? res : (res?.data ?? res);
            const items: any[] = Array.isArray(raw?.[0]) ? raw[0] : (Array.isArray(raw) ? raw : []);
            this.allPaymentRows = items.map(p => ({
              chargeDate:        this.formatDate(p.charge_date ?? ''),
              month:             p.month != null ? String(p.month) : '',
              total:             p.total != null ? `${p.currency_id ?? ''} ${p.total}` : '',
              status:            PAYMENT_STATUS[p.status] ?? String(p.status ?? ''),
              authorizationCode: p.authorization_code ?? '-',
              comment:           p.comment ?? '-'
            }));
            this.paymentTotalItems = this.allPaymentRows.length;
            this.updatePaymentPage();
          },
          error: () => {
            this.allPaymentRows = [];
            this.paymentTotalItems = 0;
            this.updatePaymentPage();
          }
        });

        this.agricolaService.getChubbTransactions(
          this.rawSaleId,
          this.basicInfo.policyNumber,
          this.basicInfo.dui
        ).subscribe({
          next: (res: any) => {
            const items: any[] = Array.isArray(res) ? res : (res?.data ?? []);
            this.allTransactionRows = items.map(t => ({
              transaction: TRANSACTION_EQUIVALENCES[t.transaction] ?? t.transaction,
              requestDate: this.formatDate(t.transaction_date),
              processDate: this.formatDate(t.creation_date),
              amount:      '',
              agencyName:  t.agency_name ?? '',
              agencyUser:  t.agency_user ?? ''
            }));
            this.transactionTotalItems = this.allTransactionRows.length;
            this.updateTransactionPage();
          },
          error: () => {
            this.allTransactionRows = [];
            this.transactionTotalItems = 0;
            this.updateTransactionPage();
          }
        });
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  // Finds, among the client's accounts, the one being charged for this sale by
  // matching the stored credit_number (last digits of the card/account) against
  // either the account number or the TDC code. Guards against an empty
  // credit_number, since ''.endsWith('') would otherwise match the first account.
  private matchCurrentPaymentAccount(): void {
    const credit = this.creditNumber?.trim();
    if (!credit || this.accountRows.length === 0) {
      this.currentPaymentAccount = null;
      return;
    }
    this.currentPaymentAccount = this.accountRows.find(
      a => a.accountNumber.endsWith(credit) || a.codCtaTDC.endsWith(credit)
    ) ?? null;
  }

  private updatePaymentPage(): void {
    const start = (this.paymentCurrentPage - 1) * this.paymentPageSize;
    this.pagedPaymentRows = this.allPaymentRows.slice(start, start + this.paymentPageSize);
  }

  private updateTransactionPage(): void {
    const start = (this.transactionCurrentPage - 1) * this.transactionPageSize;
    this.pagedTransactionRows = this.allTransactionRows.slice(start, start + this.transactionPageSize);
  }

  private buildPageRange(current: number, total: number): number[] {
    const pages: number[] = [];
    let start = Math.max(1, current - 1);
    const end = Math.min(total, start + 3);
    if (end - start < 3) start = Math.max(1, end - 3);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  private formatDate(value: string | null): string {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${d.getFullYear()}`;
  }

  private mapGender(value: number | null | undefined): string {
    if (value === 0) return 'Femenino';
    if (value === 1) return 'Masculino';
    return '';
  }

  private mapRelationshipStatus(value: number | null | undefined): string {
    const map: Record<number, string> = { 1: 'Soltero/a', 2: 'Casado/a', 3: 'Divorciado/a', 4: 'Viudo/a' };
    return value != null ? (map[value] ?? '') : '';
  }

  private mapMemberClassification(value: number | null | undefined): string {
    const map: Record<number, string> = {
      10: 'Black List', 20: 'Top Offender', 30: 'VIP',
      50: 'Discapacitado', 60: 'Top Offender Gasolina', 250: 'Colaborador-Sponsor'
    };
    return value != null ? (map[value] ?? 'Normal') : 'Normal';
  }

  private buildMovementsLabel(current: number, pageSize: number, total: number): string {
    const end = Math.min(current * pageSize, total);
    const start = (current - 1) * pageSize + 1;
    return `Mostrando ${end - start + 1} movimientos de ${total}`;
  }


}
